import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { RoleProvider } from "./contexts/RoleContext";
import { ClientProvider } from "./contexts/ClientContext";
import { Layout } from "./components/Layout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { RoleBasedDashboard } from "./pages/RoleBasedDashboard";
import { PartnerDashboard } from "./pages/partner/PartnerDashboard";
import { ClientDetail } from "./pages/ClientDetail";
import { Billing } from "./pages/Billing";
import { ClientDashboard } from "./pages/client/ClientDashboard";
import { InstanceList } from "./pages/client/InstanceList";
import { InstanceWizard } from "./pages/client/InstanceWizard";
import { InstanceDetail } from "./pages/client/InstanceDetail";
import { BoBMarketplace } from "./pages/client/BoBMarketplace";
import { Subscription } from "./pages/client/Subscription";
import { Users } from "./pages/client/Users";
import { SoloDashboard } from "./pages/solo/SoloDashboard";
import { SoloBilling } from "./pages/solo/SoloBilling";
import { LoginHybrid } from "./pages/LoginHybrid";
import { Login } from "./pages/Login";
import { GoogleCallback } from "./pages/GoogleCallback";
import { Register } from "./pages/Register";
import { SoloRegister } from "./pages/SoloRegister";
import { VerifyEmail } from "./pages/VerifyEmail";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { AuthCallback } from "./pages/AuthCallback";
import { Privacy } from "./pages/Privacy";
import { Terms } from "./pages/Terms";
import { NotFound } from "./pages/NotFound";
import { ProtectedRoute } from "./components/ProtectedRoute";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/register-solo" element={<SoloRegister />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/google/callback" element={<GoogleCallback />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Root redirects to role-based dashboard */}
        <Route index element={<RoleBasedDashboard />} />

        {/* Partner Routes */}
        <Route path="partner">
          <Route path="dashboard" element={<PartnerDashboard />} />
          <Route path="clients/:clientId" element={<ClientDetail />} />
          <Route path="billing" element={<Billing />} />
        </Route>

        {/* Client Routes */}
        <Route path="client">
          <Route path="dashboard" element={<ClientDashboard />} />
          <Route path="instances" element={<InstanceList />} />
          <Route path="instances/create" element={<InstanceWizard />} />
          <Route path="instances/:instanceId" element={<InstanceDetail />} />
          <Route
            path="instances/:instanceId/bobs"
            element={<BoBMarketplace />}
          />
          <Route path="subscription" element={<Subscription />} />
          <Route path="users" element={<Users />} />
        </Route>

        {/* Solo Routes - Direct customers with prepaid billing */}
        <Route path="solo">
          <Route path="dashboard" element={<SoloDashboard />} />
          <Route path="instances" element={<InstanceList />} />
          <Route path="instances/create" element={<InstanceWizard />} />
          <Route path="instances/:instanceId" element={<InstanceDetail />} />
          <Route
            path="instances/:instanceId/bobs"
            element={<BoBMarketplace />}
          />
          <Route path="billing" element={<SoloBilling />} />
        </Route>
      </Route>

      {/* Catch all - show 404 page */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <RoleProvider>
              <ClientProvider>
                <Toaster
                  position="top-right"
                  richColors
                  closeButton
                  duration={5000}
                  toastOptions={{
                    style: {
                      background: "hsl(222.2 84% 4.9%)",
                      color: "hsl(210 40% 98%)",
                      border: "1px solid hsl(217.2 32.6% 17.5%)",
                    },
                  }}
                />
                <AppRoutes />
              </ClientProvider>
            </RoleProvider>
          </AuthProvider>
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
